import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Save, Truck, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDelivery() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings/delivery').then(r => setSettings(r.data.value)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/delivery', { value: settings });
      toast.success('Delivery settings saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="flex items-center justify-center h-full p-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-delivery-page" className="p-6 lg:p-8 max-w-2xl">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-serif font-medium">Delivery Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure delivery charges and areas</p>
      </div>

      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /> Delivery Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="flex items-center gap-2"><IndianRupee className="h-4 w-4" /> Delivery Charge (flat rate)</Label>
            <Input data-testid="delivery-charge-input" type="number" value={settings.delivery_charge || 0}
              onChange={e => setSettings({...settings, delivery_charge: parseFloat(e.target.value) || 0})}
              className="mt-1 w-48" />
            <p className="text-xs text-muted-foreground mt-1">Applied to every order. Set to 0 for free delivery.</p>
          </div>

          <div>
            <Label className="flex items-center gap-2"><IndianRupee className="h-4 w-4" /> Platform Fee</Label>
            <Input data-testid="platform-fee-input" type="number" value={settings.platform_fee || 0}
              onChange={e => setSettings({...settings, platform_fee: parseFloat(e.target.value) || 0})}
              className="mt-1 w-48" />
            <p className="text-xs text-muted-foreground mt-1">Added to every order. Default Rs 10. Set to 0 to disable.</p>
          </div>

          <div>
            <Label>Free Delivery Above (order value)</Label>
            <Input data-testid="free-above-input" type="number" value={settings.free_delivery_above || 0}
              onChange={e => setSettings({...settings, free_delivery_above: parseFloat(e.target.value) || 0})}
              className="mt-1 w-48" />
            <p className="text-xs text-muted-foreground mt-1">Orders above this amount get free delivery. Set to 0 to disable.</p>
          </div>

          <div>
            <Label>Minimum Order Value</Label>
            <Input data-testid="min-order-input" type="number" value={settings.min_order_value || 0}
              onChange={e => setSettings({...settings, min_order_value: parseFloat(e.target.value) || 0})}
              className="mt-1 w-48" />
            <p className="text-xs text-muted-foreground mt-1">Minimum cart value required to place an order. Set to 0 for no minimum.</p>
          </div>

          <Button data-testid="save-delivery-settings" onClick={handleSave} className="rounded-full" disabled={saving}>
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
